import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import BreakdownTopValuesControl from "./BreakdownTopValuesControl";
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from "features/activity/styles/dataTableStyles";

const tableStaticRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

function formatCohortCalendarLabel(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dt);
    }
  }
  const m2 = s.match(/^(\d{4})-(\d{2})$/);
  if (m2) {
    const y = Number(m2[1]);
    const mo = Number(m2[2]) - 1;
    const dt = new Date(y, mo, 1);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(dt);
    }
  }
  return s || "—";
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pct(n) {
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2)}%`;
}

function median(values) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
  return sorted[mid];
}

/**
 * Table for retention "Line" visualization (calendar cohort columns).
 * Columns: Series | Average | cohort-date buckets (same as x-axis).
 * Rows: one per series (currently overall only; breakdown-ready).
 */
function lineBreakdownRowKey(segment) {
  const s = String(segment ?? "").trim() || "(none)";
  return `bd-line-${encodeURIComponent(s).replace(/%/g, "_")}`;
}

function compareLineBreakdownRows(a, b, breakdownSortMode) {
  if (breakdownSortMode === "segment-asc") {
    return String(a.segment || "").localeCompare(String(b.segment || ""), undefined, { sensitivity: "base" });
  }
  if (breakdownSortMode === "segment-desc") {
    return String(b.segment || "").localeCompare(String(a.segment || ""), undefined, { sensitivity: "base" });
  }
  const ascending = breakdownSortMode === "average-asc";
  const sa = Number(a.summary);
  const sb = Number(b.summary);
  const aOk = Number.isFinite(sa);
  const bOk = Number.isFinite(sb);
  if (!aOk && !bOk) {
    return String(a.segment || "").localeCompare(String(b.segment || ""), undefined, { sensitivity: "base" });
  }
  if (!aOk) return 1;
  if (!bOk) return -1;
  if (sa === sb) {
    return String(a.segment || "").localeCompare(String(b.segment || ""), undefined, { sensitivity: "base" });
  }
  return ascending ? sa - sb : sb - sa;
}

export default function RetentionLineTable({
  cohorts = [],
  breakdowns = [],
  /** When breakdown rows exist, used for first column header (Mixpanel-style) */
  breakdownDimensionLabel = "",
  metric = "retention_rate",
  intervals = [],
  retentionGroupIndex = 0,
  seriesLabel = "Overall",
  breakdownChartTopN,
  onBreakdownChartTopNChange,
  boardCompact = false,
}) {
  const [summaryMode, setSummaryMode] = useState("average");
  const [summaryMenuOpen, setSummaryMenuOpen] = useState(false);
  const [summarySort, setSummarySort] = useState("asc");
  /** segment-asc | segment-desc | average-asc | average-desc — line view breakdown only */
  const [breakdownSortMode, setBreakdownSortMode] = useState("segment-asc");
  const [breakdownSortMenuOpen, setBreakdownSortMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const breakdownSortTriggerRef = useRef(null);
  const breakdownSortMenuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [breakdownMenuPos, setBreakdownMenuPos] = useState({ top: 0, left: 0 });

  const intervalIdx =
    intervals.length > 0
      ? Math.min(Math.max(0, retentionGroupIndex), intervals.length - 1)
      : 0;

  const orderedCohorts = useMemo(() => {
    const rows = (cohorts || []).filter((c) => c && !c.isSummary);
    return [...rows].sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""))
    );
  }, [cohorts]);

  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;

  const columnCells = useMemo(() => {
    // Columns are calendar buckets (cohort dates). Keep chronological order.
    return orderedCohorts.map((c) => ({
      rawLabel: c.label || "—",
      label: formatCohortCalendarLabel(c.label),
    }));
  }, [orderedCohorts]);

  const overallRow = useMemo(() => {
    const cells = orderedCohorts.map((c) => {
      const v = c?.values?.[intervalIdx];
      const n = safeNum(v);
      return {
        rawLabel: c.label || "—",
        label: formatCohortCalendarLabel(c.label),
        cohortSize: safeNum(c.cohortSize) ?? 0,
        value: n ?? 0,
        isIncomplete:
          Array.isArray(c.isIncomplete) && c.isIncomplete[intervalIdx] === true,
      };
    });
    return { segment: seriesLabel, cells };
  }, [intervalIdx, orderedCohorts, seriesLabel]);

  const breakdownRows = useMemo(() => {
    if (!hasBreakdowns) return [];
    const labels = orderedCohorts.map((c) => String(c?.label || ""));

    return (breakdowns || []).map((bd) => {
      const map = new Map();
      (bd?.cohorts || []).forEach((c) => {
        const key = String(c?.label || "");
        if (key) map.set(key, c);
      });

      const cells = labels.map((label) => {
        const segCohort = map.get(label) || null;
        const denomRaw = segCohort?.cohortSize;
        const denom =
          typeof denomRaw === "number" && !Number.isNaN(denomRaw) ? denomRaw : parseInt(denomRaw, 10) || 0;
        const retainedRaw = Array.isArray(segCohort?.retained) ? segCohort.retained[intervalIdx] : 0;
        const retained = retainedRaw != null ? Number(retainedRaw) : 0;
        const isCompleteArr = Array.isArray(segCohort?.isComplete) ? segCohort.isComplete : [];
        const isIncomplete = isCompleteArr[intervalIdx] === false;
        const value = metric === "retention_rate" ? (denom > 0 ? (retained / denom) * 100 : 0) : retained;

        return {
          rawLabel: label || "—",
          label: formatCohortCalendarLabel(label),
          cohortSize: denom,
          value: safeNum(value) ?? 0,
          isIncomplete,
        };
      });

      return { segment: bd?.segment || "(none)", cells };
    });
  }, [breakdowns, hasBreakdowns, intervalIdx, metric, orderedCohorts]);

  useEffect(() => {
    if (!summaryMenuOpen) return undefined;
    const updateMenuPos = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 196;
      const menuHeight = 292;
      const gap = 8;
      const maxLeft = Math.max(gap, window.innerWidth - menuWidth - gap);
      const clampedLeft = Math.max(gap, Math.min(rect.left, maxLeft));
      // Keep the popup above the header; clamp to viewport top so it remains fully visible.
      const computedTop = Math.max(gap, rect.top - menuHeight - gap);
      setMenuPos({
        top: computedTop,
        left: clampedLeft,
      });
    };
    updateMenuPos();
    const onDocMouseDown = (event) => {
      const t = event.target;
      if (menuRef.current && menuRef.current.contains(t)) return;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      setSummaryMenuOpen(false);
    };
    const onDocKeyDown = (event) => {
      if (event.key === "Escape") setSummaryMenuOpen(false);
    };
    const onWinScroll = () => updateMenuPos();
    const onWinResize = () => updateMenuPos();
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    window.addEventListener("scroll", onWinScroll, true);
    window.addEventListener("resize", onWinResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
      window.removeEventListener("scroll", onWinScroll, true);
      window.removeEventListener("resize", onWinResize);
    };
  }, [summaryMenuOpen]);

  useEffect(() => {
    if (!breakdownSortMenuOpen) return undefined;
    const updatePos = () => {
      const el = breakdownSortTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBreakdownMenuPos({ top: r.bottom + 4, left: r.left });
    };
    updatePos();
    const onDoc = (e) => {
      const t = e.target;
      if (breakdownSortMenuRef.current?.contains(t)) return;
      if (breakdownSortTriggerRef.current?.contains(t)) return;
      setBreakdownSortMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setBreakdownSortMenuOpen(false);
    };
    const onScroll = () => updatePos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [breakdownSortMenuOpen]);

  const summaryLabelMap = {
    average: "Average",
    sum: "Sum",
    min: "Min",
    max: "Max",
    median: "Median",
  };

  const computeSummary = (cells) => {
    const values = (cells || []).map((r) => Number(r.value) || 0);
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, v) => acc + v, 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const med = median(values);

    if (summaryMode === "sum") return sum;
    if (summaryMode === "min") return min;
    if (summaryMode === "max") return max;
    if (summaryMode === "median") return med;

    // Default: weighted average for retention rate, arithmetic average for users.
    if (metric === "retention_rate") {
      const denom = (cells || []).reduce((acc, r) => acc + (r.cohortSize || 0), 0);
      if (denom <= 0) return 0;
      const numer = (cells || []).reduce((acc, r) => acc + (r.value || 0) * (r.cohortSize || 0), 0);
      return numer / denom;
    }
    return sum / values.length;
  };

  const rowsForTable = useMemo(() => {
    const base = hasBreakdowns ? breakdownRows : [overallRow];
    const withSummary = base.map((r) => ({ ...r, summary: computeSummary(r.cells) }));
    if (hasBreakdowns) {
      const copy = [...withSummary];
      copy.sort((a, b) => compareLineBreakdownRows(a, b, breakdownSortMode));
      return copy;
    }
    return summarySort === "desc"
      ? [...withSummary].sort((a, b) => (b.summary || 0) - (a.summary || 0))
      : [...withSummary].sort((a, b) => (a.summary || 0) - (b.summary || 0));
  }, [
    breakdownRows,
    breakdownSortMode,
    hasBreakdowns,
    overallRow,
    summarySort,
    summaryMode,
    metric,
  ]);

  const breakdownFirstColLabel =
    String(breakdownDimensionLabel || "").trim() || "Segment";

  if (columnCells.length === 0 || rowsForTable.length === 0) {
    return (
      <div className="w-full p-4 text-center text-sm" style={{ color: theme.textMuted }}>
        No data available
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={tableHeaderRowClass}>
            <th
              className={tableHeaderCellClass}
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              {hasBreakdowns ? (
                <div className="flex flex-col items-start gap-1" style={{ textTransform: "none" }}>
                  <div className="inline-flex items-center gap-1" ref={breakdownSortTriggerRef}>
                  <span>
                    {breakdownFirstColLabel}
                    {breakdownRows.length > 0 ? ` ${breakdownRows.length}` : ""}
                  </span>
                  <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={breakdownSortMenuOpen}
                    onClick={() => setBreakdownSortMenuOpen((o) => !o)}
                    className="inline-flex items-center justify-center rounded p-0.5"
                    style={{
                      color: theme.textMuted,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                    title="Sort breakdown"
                  >
                    <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {breakdownSortMenuOpen &&
                    createPortal(
                      <div
                        ref={breakdownSortMenuRef}
                        role="menu"
                        style={{
                          position: "fixed",
                          top: breakdownMenuPos.top,
                          left: breakdownMenuPos.left,
                          minWidth: 200,
                          padding: "8px 0",
                          backgroundColor: theme.bgRaised || "var(--surface-base)",
                          border: `1px solid ${theme.border}`,
                          borderRadius: 10,
                          boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
                          zIndex: 9999,
                          fontFamily: "Inter, sans-serif",
                          textTransform: "none",
                        }}
                      >
                        {[
                          {
                            key: "seg-asc",
                            label: "Segment A → Z",
                            active: breakdownSortMode === "segment-asc",
                            onSelect: () => {
                              setBreakdownSortMode("segment-asc");
                              setBreakdownSortMenuOpen(false);
                            },
                          },
                          {
                            key: "seg-desc",
                            label: "Segment Z → A",
                            active: breakdownSortMode === "segment-desc",
                            onSelect: () => {
                              setBreakdownSortMode("segment-desc");
                              setBreakdownSortMenuOpen(false);
                            },
                          },
                          {
                            key: "avg-asc",
                            label: `${summaryLabelMap[summaryMode] || "Average"} 1 → 100`,
                            active: breakdownSortMode === "average-asc",
                            onSelect: () => {
                              setBreakdownSortMode("average-asc");
                              setSummarySort("asc");
                              setBreakdownSortMenuOpen(false);
                            },
                          },
                          {
                            key: "avg-desc",
                            label: `${summaryLabelMap[summaryMode] || "Average"} 100 → 1`,
                            active: breakdownSortMode === "average-desc",
                            onSelect: () => {
                              setBreakdownSortMode("average-desc");
                              setSummarySort("desc");
                              setBreakdownSortMenuOpen(false);
                            },
                          },
                        ].map((opt) => (
                          <div
                            key={opt.key}
                            role="menuitem"
                            onClick={opt.onSelect}
                            className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                            style={{
                              backgroundColor: opt.active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                              color: opt.active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                              fontWeight: opt.active ? 600 : 500,
                              borderLeft: opt.active ? `2px solid ${theme.primary}` : "2px solid transparent",
                            }}
                          >
                            {opt.label}
                          </div>
                        ))}
                      </div>,
                      document.body
                    )}
                  </div>
                  {typeof onBreakdownChartTopNChange === "function" && breakdownChartTopN != null && (
                    <BreakdownTopValuesControl value={breakdownChartTopN} onChange={onBreakdownChartTopNChange} />
                  )}
                </div>
              ) : (
                "Retention"
              )}
            </th>
            <th
              className={tableHeaderCellClass}
              style={{ borderRight: `1px solid ${theme.border}`, position: "relative" }}
            >
              <div
                ref={triggerRef}
                role="button"
                tabIndex={0}
                aria-haspopup="menu"
                aria-expanded={summaryMenuOpen}
                onClick={() => setSummaryMenuOpen((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSummaryMenuOpen((v) => !v);
                  }
                }}
                className="inline-flex items-center gap-1 cursor-pointer"
                style={{ color: theme.textMuted, fontWeight: 600, textTransform: "none" }}
                title="Summary options"
              >
                <span>{summaryLabelMap[summaryMode] || "Average"}</span>
                <svg width="10" height="10" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              {summaryMenuOpen && createPortal(
                <div
                  ref={menuRef}
                  role="menu"
                  style={{
                    position: "fixed",
                    top: menuPos.top,
                    left: menuPos.left,
                    width: 196,
                    backgroundColor: theme.bgRaised || "var(--surface-base)",
                    border: `1px solid ${theme.border}`,
                    borderRadius: 10,
                    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
                    zIndex: 9999,
                    padding: "8px 0",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  <div className="px-3 pb-1 text-[11px] font-semibold" style={{ color: theme.textMuted, textTransform: "none" }}>
                    Summary
                  </div>
                  {[
                    { key: "average", label: "Average" },
                    { key: "sum", label: "Sum" },
                    { key: "min", label: "Min" },
                    { key: "max", label: "Max" },
                    { key: "median", label: "Median" },
                  ].map((opt) => {
                    const active = summaryMode === opt.key;
                    return (
                      <div
                        key={opt.key}
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          setSummaryMode(opt.key);
                          setSummaryMenuOpen(false);
                        }}
                        className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                        style={{
                          backgroundColor: active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                          color: active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                          fontWeight: active ? 600 : 500,
                          textTransform: "none",
                          borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "var(--surface-sunken)";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {opt.label}
                      </div>
                    );
                  })}
                  {!hasBreakdowns && (
                    <>
                      <div className="mx-2 my-1" style={{ borderTop: `1px solid ${theme.border}` }} />
                      <div className="px-3 pb-1 text-[11px] font-semibold" style={{ color: theme.textMuted, textTransform: "none" }}>
                        Sort
                      </div>
                      {[
                        { key: "asc", label: "1 \u2192 100" },
                        { key: "desc", label: "100 \u2192 1" },
                      ].map((opt) => {
                        const active = summarySort === opt.key;
                        return (
                          <div
                            key={opt.key}
                            role="menuitemradio"
                            aria-checked={active}
                            onClick={() => {
                              setSummarySort(opt.key);
                              setSummaryMenuOpen(false);
                            }}
                            className="mx-2 px-3 py-2 text-[13px] cursor-pointer rounded"
                            style={{
                              backgroundColor: active ? (theme.bgOverlay || "var(--surface-sunken)") : "transparent",
                              color: active ? (theme.textPrimary || "var(--text-primary)") : (theme.textSecondary || "var(--text-secondary)"),
                              fontWeight: active ? 600 : 500,
                              textTransform: "none",
                              borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "var(--surface-sunken)";
                            }}
                            onMouseLeave={(e) => {
                              if (!active) e.currentTarget.style.backgroundColor = "transparent";
                            }}
                          >
                            {summaryLabelMap[summaryMode] || "Average"} {opt.label}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>,
                document.body
              )}
            </th>
            {columnCells.map((c, i) => (
              <th
                key={i}
                className={tableHeaderCellClass}
                style={{
                  borderRight: i < columnCells.length - 1 ? `1px solid ${theme.border}` : "none",
                  minWidth: 72,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsForTable.map((row, rowIdx) => (
            <tr
              key={hasBreakdowns ? lineBreakdownRowKey(row.segment) : rowIdx}
              className={tableStaticRowClass}
            >
              <td
                className={`${tableDataCellClass} whitespace-nowrap`}
                style={{
                  color: theme.text,
                  borderRight: `1px solid ${theme.border}`,
                  fontWeight: 500,
                  minWidth: 160,
                }}
                title={row.segment}
              >
                {row.segment}
              </td>
              <td
                className={`${tableDataCellClass} text-right tabular-nums whitespace-nowrap`}
                style={{ color: theme.text, borderRight: `1px solid ${theme.border}`, fontWeight: 600 }}
              >
                {metric === "retention_rate" ? pct(row.summary) : formatNumber(row.summary)}
              </td>
              {(row.cells || []).map((c, i) => (
                <td
                  key={i}
                  className={`${tableDataCellClass} text-right tabular-nums whitespace-nowrap`}
                  style={{
                    color: theme.text,
                    borderRight: i < (row.cells || []).length - 1 ? `1px solid ${theme.border}` : "none",
                    opacity: c.isIncomplete ? 0.7 : 1,
                  }}
                  title={c.cohortSize ? `${formatNumber(c.cohortSize)} users` : undefined}
                >
                  {metric === "retention_rate" ? pct(c.value) : formatNumber(c.value)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

